import React from 'react';
import {
  createFacebookLink,
  createFacebookMessengerLink,
  createTwitterLink,
  createEmailLink,
  createWhatsAppLink,
} from 'utils/shareLinksUtils';
import { ITranslate } from 'locale';
import {
  IAutocompleteAddress,
  IBulletin,
  IDeal,
  IProject,
  ICommercialBulletin,
  ICommercialProject,
  PoiType,
  BulletinPoiType,
} from 'utils/entities';
import CopyLinkIcon from 'assets/svg/link.svg';
import CopyLink2Icon from 'assets/svg/link2.svg';
import EmailIcon from 'assets/svg/mail.svg';
import Email2Icon from 'assets/svg/mail2.svg';
import TwitterIcon from 'assets/svg/twitter.svg';
import FacebookIcon from 'assets/svg/fb.svg';
import Facebook2Icon from 'assets/svg/fb2.svg';
import FacebookMessengerIcon from 'assets/svg/messenger.svg';
import WhatsAppIcon from 'assets/svg/whatsapp.svg';
import WhatsApp2Icon from 'assets/svg/whatsapp2.svg';
import { isBulletinPoiType, isProjectPoiType, routeByPoiType } from 'utils/marketplaceRoutes';
import { isServer } from 'utils';
import { RouteContext } from 'react-router5/types/types';


export enum ShareType {
  Email = 'email',
  CopyLink = 'copyLink',
  Twitter = 'twitter',
  Facebook = 'facebook',
  FacebookMessenger = 'facebookMessenger',
  WhatsApp = 'whatsApp',
}

export const shareActionTypeToIcon: Record<ShareType, JSX.Element> = {
  [ShareType.Email]: <EmailIcon />,
  [ShareType.CopyLink]: <CopyLinkIcon />,
  [ShareType.Twitter]: <TwitterIcon />,
  [ShareType.Facebook]: <FacebookIcon />,
  [ShareType.FacebookMessenger]: <FacebookMessengerIcon />,
  [ShareType.WhatsApp]: <WhatsAppIcon />,
};

export const shareNewActionTypeToIcon: Record<ShareType, JSX.Element> = {
  [ShareType.Email]: <Email2Icon />,
  [ShareType.CopyLink]: <CopyLink2Icon />,
  [ShareType.Twitter]: <TwitterIcon />,
  [ShareType.Facebook]: <Facebook2Icon />,
  [ShareType.FacebookMessenger]: <FacebookMessengerIcon />,
  [ShareType.WhatsApp]: <WhatsApp2Icon />,
};

interface ShareHandlerOptions {
  type: ShareType;
  url: string;
  text?: string;
  redirectUri?: string;
  shareViaEmailOptions?: ShareViaEmailOptions;
  shareViaWhatsAppOptions?: ShareViaWhatsAppOptions;
}


export function clearParamsBeforeShare(url: string) {
  const urlObj = new URL(url);
  const params = new URLSearchParams(urlObj.search);

  params.delete('activeListingImage');

  const searchP = params.toString();

  return urlObj.origin.concat(urlObj.pathname).concat(searchP ? '?' + searchP : '');
}

export const shareHandler = (options: ShareHandlerOptions) => {
  const { type, url, redirectUri, text, shareViaEmailOptions, shareViaWhatsAppOptions } = options;

  switch (type) {
    case ShareType.Facebook: {
      const href = createFacebookLink(url);
      window.open(href);
      break;
    }
    case ShareType.FacebookMessenger: {
      const href = createFacebookMessengerLink(url, { redirectUri });
      window.open(href);
      break;
    }
    case ShareType.Twitter: {
      const href = createTwitterLink(url, { text });
      window.open(href);
      break;
    }
    case ShareType.WhatsApp: {
      const urlObj = new URL(url);
      const { t } = shareViaEmailOptions;

      if (!shareViaWhatsAppOptions || !shareViaWhatsAppOptions.preserveParams) urlObj.search = '';

      const whatsAppText = shareViaWhatsAppOptions && shareViaWhatsAppOptions.noIntroText ? urlObj.toString() : `${t('shareButton.whatsappIntroText')}:\n${urlObj.toString()}`;
      const href = createWhatsAppLink({ text: whatsAppText });

      window.open(href);
      break;
    }
    case ShareType.Email: {
      shareViaEmail(shareViaEmailOptions);
      break;
    }
  }
};

export interface ShareEmailSlimBulletin {
  id: string;
  type: 'ShareEmailSlimBulletin';
  bulletinType: BulletinPoiType;
}

const isShareEmailSlimBulletin = (poi: ShareViaEmailOptions['poi']): poi is ShareEmailSlimBulletin => {
  return poi.type === 'ShareEmailSlimBulletin';
};

interface ShareViaEmailOptions {
  t: ITranslate;
  url?: string;
  poi?: IBulletin | IProject | IDeal | ICommercialBulletin | ICommercialProject | ShareEmailSlimBulletin;
  addressDocEntry?: IAutocompleteAddress;
  subject?: string;
  body?: string;
}

interface ShareViaWhatsAppOptions {
  preserveParams?: boolean;
  noIntroText?: boolean;
}

export const shareViaEmail = (options: ShareViaEmailOptions) => {
  const { t, poi, addressDocEntry, url: shareUrl , subject: shareSubject, body: shareBody } = options;
  let subject: string;
  let body: string;

  if (shareSubject || shareBody) {
    subject = shareSubject;
    body = shareBody;
  }
  else if (poi) {
    const link = shareUrl || window.location.href;
    const url = `${link}${link.indexOf('?') === -1 ? '?' : '&'}share_id=${poi.id}`;
    if (poi.type === 'deal') {
      subject = t('soldPage.shareEmailSubject');
      body = t('soldPage.shareEmailBody', { url });
    }
    else if (isShareEmailSlimBulletin(poi)) {
      subject = t('shareButton.emailSubject');
      body = t('shareButton.emailBody.simple', { url });
    }
    else if (isBulletinPoiType(poi)) {
      const { streetNumber, streetName } = poi.structuredAddress || {};
      const primaryAddress = t('unitPage.primaryAddress', {
        streetNumber,
        streetName,
        neighbourhood: null,
      });
      subject = t('shareButton.emailSubject');
      body = t('shareButton.emailBody', { primaryAddress, url });
    }
    else if (isProjectPoiType(poi)) {
      subject = poi.projectName;
      body = url;
    }
  }
  else if (addressDocEntry) {
    const link = shareUrl || window.location.href;
    const url = `${link}${link.indexOf('?') === -1 ? '?' : '&'}share_id=${addressDocEntry.docId}`;
    const line1 = t('addressPage.addressLine1', addressDocEntry as any);
    const line2 = t('addressPage.addressLine2', addressDocEntry as any);
    subject = t('shareButton.emailSubjectAddressPage', { line1, line2 });
    body = t('shareButton.emailBodyAddressPage', { line1, line2, url });
  }

  if (subject && body) {
    const href = createEmailLink({ subject, body });
    window.location.href = href;
  }
};

export const cardShareUrl = (routeContext: RouteContext, poiId: string, poiType: PoiType) => {
  const url = isServer() ? null : window.location.origin + routeContext.router.buildUrl(routeByPoiType(poiType), { id: poiId });
  return url;
};

export const handleCardShare = (streetName: string, streetNumber: string, url: string, t: ITranslate) => {
  const primaryAddress = t('unitPage.primaryAddress', {
    streetNumber,
    streetName,
    neighbourhood: null,
  });

  return (shareType: ShareType) => {
    if (shareType === ShareType.Email) {
      const subjectText = t('shareButton.emailSubject');
      const bodyText = t('shareButton.emailBody', { primaryAddress, url });
      const uri = `mailto:?subject=${subjectText}&body=${bodyText}`;
      window.location.href = encodeURI(uri);
    }
    else {
      shareHandler({ type: shareType, url });
    }
  };
};
